import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ROOT_FOLDER_ID, handleRealUsedFlag, RealUsedFlagSuffix } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';

import { getStaticItems, gotoLibraryPage, isLibraryPageSelected } from './utils';

export const useAdStudioNavigation = ({
  history,
  rootRoute,
  dataService,
  onDropToFolder: onDropToFolderProp,
  permissions,
  adsStudioAccountPreferences,
}) => {
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [isCopilotEnabled, setIsCopilotEnabled] = useState(false);
  const serverContext = useContext(ServerContext);

  const isBrandKitEnabled = permissions && permissions.dynamic && permissions.dynamic.BrandKitUI;

  const {
    folderPathTree,
    loadFolderPathTreeItem,
    loadMoreFolderPathTreeItem,
    initializeFolderPathTree,
    navigateIntoFolderItem,
    navigateIntoRootFolder,
    currentFolder,
  } = dataService;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const staticItems = useMemo(() => getStaticItems(history, rootRoute, navigateIntoFolderItem, isCopilotEnabled), [history.location.pathname, rootRoute, navigateIntoFolderItem, isCopilotEnabled]);

  const handleFolderItemClicked = useCallback((folder) => {
    handleRealUsedFlag(adsStudioAccountPreferences, RealUsedFlagSuffix.App);
    const { folderId } = folder;
    setSelectedItemId(folderId);
    setSelectedFolderId(folderId);
    gotoLibraryPage(history, rootRoute, history.location.search);
    navigateIntoFolderItem({ folder });
  }, [adsStudioAccountPreferences, history, navigateIntoFolderItem, rootRoute]);

  const onDropToFolder = useCallback((folder) => {
    onDropToFolderProp(folder);
  }, [onDropToFolderProp]);

  useEffect(() => {
    if (staticItems.length > 0) {
      const selectedItem = staticItems.find(item => item.isSelected);
      if (selectedItem) {
        if (selectedFolderId && isLibraryPageSelected(history.location.pathname, rootRoute)) {
          // if select folder, then the folder should have selected status instead of the library item
          setSelectedItemId(selectedFolderId);
        } else {
          setSelectedItemId(selectedItem.id);
        }
      }
    }
  }, [staticItems, history, selectedFolderId, rootRoute]);

  useEffect(() => {
    if (!selectedItemId) {
      initializeFolderPathTree();
    }
  }, [initializeFolderPathTree, selectedItemId]);

  useEffect(() => {
    if (currentFolder && currentFolder.folderId !== ROOT_FOLDER_ID && isLibraryPageSelected(history.location.pathname, rootRoute)) {
      setSelectedFolderId(currentFolder.folderId);
      setSelectedItemId(currentFolder.folderId);
    } else if (currentFolder && currentFolder.folderId === ROOT_FOLDER_ID) {
      setSelectedFolderId(null);
      const selectedItem = staticItems.find(item => item.isSelected);
      if (selectedItem) {
        setSelectedItemId(selectedItem.id);
      }
    }
  }, [currentFolder, history.location.pathname, rootRoute, staticItems]);

  useEffect(() => {
    if (isBrandKitEnabled) {
      isCopilotRegionRestricted({
        feature: 'Recommendations',
        permissions,
        serverContext,
      }).then(isRestricted => setIsCopilotEnabled(!isRestricted));
    } else {
      setIsCopilotEnabled(false);
    }
  }, [isBrandKitEnabled, permissions, serverContext]);

  return {
    staticItems,
    selectedItemId,
    setSelectedItemId,
    selectedFolderId,
    setSelectedFolderId,
    folderPathTree: folderPathTree[0],
    loadFolderPathTreeItem,
    loadMoreFolderPathTreeItem,
    handleFolderItemClicked,
    onDropToFolder,
    navigateIntoRootFolder,
  };
};
